import React from "react";
import { Check } from "lucide-react";
import SectionHeading from "./SectionHeading";
import Button from "./Button";

const PRICING_GROUPS = [
  {
    id: "membership",
    eyebrow: "Monthly Membership",
    title: "Train consistently, compete together, and grow every week.",
    description:
      "Our core academy membership is built for players who want structured development, match experience, and a clear pathway through the season.",
    cards: [
      {
        name: "Standard Package",
        price: "$80",
        cadence: "/month",
        featured: true,
        features: ["Train 2 days a week", "Participation in league games"],
      },
      {
        name: "Individual 1-on-1 Session",
        price: "$50",
        cadence: "/hour",
        featured: false,
        features: [
          "Position-specific coaching",
          "Focused technical improvement",
          "Flexible alternative or add-on",
        ],
      },
    ],
  },
  {
    id: "gear",
    eyebrow: "Gear & Uniforms",
    title: "One-time academy essentials for training and league play.",
    description:
      "These items are separate from the monthly membership and help keep every player ready for practice and match day.",
    cards: [
      {
        name: "Full Uniform Set",
        price: "$50",
        cadence: "one-time",
        featured: false,
        features: [
          "Academy match-ready set",
          "Required kit for official sessions",
        ],
      },
      {
        name: "Practice Kit",
        price: "$10",
        cadence: "one-time",
        featured: false,
        features: ["Training-ready basics", "Affordable starter option"],
      },
    ],
  },
];

const FeatureList = ({ features }) => (
  <ul className="space-y-3">
    {features.map((feature) => (
      <li
        key={feature}
        className="flex items-start gap-3 text-sm text-gray-600 font-inter leading-relaxed"
      >
        <Check
          className="mt-0.5 h-4 w-4 shrink-0 text-[#7D192A]"
          strokeWidth={3}
        />
        <span>{feature}</span>
      </li>
    ))}
  </ul>
);

const PricingCard = ({ card }) => (
  <div
    className={`surface-card rounded-3xl p-6 md:p-7 shadow-soft h-full flex flex-col ${
      card.featured
        ? "border-2 border-[#F4B400] relative"
        : "border border-black/5"
    }`}
  >
    <div className="flex items-end gap-1.5">
      <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-inter font-extrabold text-black leading-none tracking-tight">
        {card.price}
      </span>
      <span className="pb-1 text-sm md:text-base font-inter text-gray-500">
        {card.cadence}
      </span>
    </div>

    <h3 className="mt-4 card-title text-[#1A1C25]">{card.name}</h3>

    <div className="mt-5">
      <FeatureList features={card.features} />
    </div>
  </div>
);

const PricingSection = ({ className = "" }) => {
  return (
    <section className={`py-16 md:py-20 lg:py-24 bg-[#f5f5f5] ${className}`}>
      <div className="max-w-7xl mx-auto site-gutter">
        <div className="mt-12 space-y-16">
          {PRICING_GROUPS.map((group) => (
            <section key={group.id} className="space-y-6">
              <div className="max-w-3xl">
                <p className="eyebrow-badge">{group.eyebrow}</p>
                <h2 className="section-heading mt-3 text-[#1A1C25] max-w-3xl">
                  {group.title}
                </h2>
                <p className="body-text text-gray-600 mt-3">
                  {group.description}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {group.cards.map((card) => (
                  <PricingCard key={card.name} card={card} />
                ))}
              </div>

              {group.id === "gear" ? (
                <p className="text-sm text-gray-600 font-inter leading-relaxed">
                  Uniforms are required for league games.{" "}
                  {/* confirm with client */}
                </p>
              ) : null}
            </section>
          ))}
        </div>

        <div className="mt-16 surface-card rounded-[2rem] p-8 md:p-10 shadow-soft border border-black/5 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <p className="font-inter font-bold uppercase tracking-[0.24em] text-[11px] text-[#7D192A]">
              Next Step
            </p>
            <h2 className="mt-3 text-xl sm:text-2xl md:text-3xl font-inter font-extrabold text-[#1A1C25] leading-tight">
              Ready to Join?
            </h2>
            <p className="body-text text-gray-600 mt-3">
              Reach out to ask about registration, availability, or which
              package best fits your player.
            </p>
          </div>

          <Button to="/contact" variant="secondary" pill className="px-6 py-3">
            CONTACT US
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
